import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  writeBatch,
  Timestamp,
  onSnapshot,
  query,
  updateDoc,
  deleteDoc,
  Unsubscribe,
} from "firebase/firestore";
import { db } from "./firebase";

// Types
export interface CompetitionMetadata {
  zurkhaiCount: number;
  onogUe: number;
  isLive: boolean;
  createdAt: Timestamp;
  createdBy: number;
  lastResetAt?: Timestamp;
  lastResetBy?: number;
  startDate?: string;
  endDate?: string;
  name?: string;
  location?: string;
}

export interface ZurkhaiMetadata {
  status: "active" | "completed" | "archived";
  refereeId?: number;
  createdAt: Timestamp;
  createdBy: number;
  completedAt?: Timestamp;
}

export interface ArcherScore {
  userId: number;
  userName: string;
  firstName: string;
  lastName: string;
  teamId?: number;
  teamName?: string;
  rankId?: number;
  rankName?: string;
  index: number;
  gender: "male" | "female";
  scores: number[]; // Total hits per оног үе (0-2)
  clicks: number[][]; // Array of clicks per оног үе: [[1, 0], [1, 1], [-1, -1]] where 1=hit, 0=miss, -1=not scored
  totalScore: number;
  hanaPoints: number[];
  hasPoints: number[];
  tovkhCount: number; // Manual/editable Товх count for tiebreaking
  isAbsent: boolean; // If archer is marked as absent
  updatedAt: Timestamp;
  updatedBy: number;
  isComplete: boolean;
}

export interface Competitor {
  id: number;
  firstName: string;
  lastName: string;
  teamId?: number;
  team?: { id: number; name: string };
  rankId?: number;
  rank?: { id: number; name: string };
  gender: "male" | "female";
}

// Helper functions to convert between nested arrays and Firestore-compatible objects
// Firestore doesn't support nested arrays, so we convert to objects
function convertClicksToFirestore(
  clicks: number[][]
): { click1: number; click2: number }[] {
  return clicks.map((clickPair) => ({
    click1: clickPair[0] ?? -1,
    click2: clickPair[1] ?? -1,
  }));
}

function convertClicksFromFirestore(firestoreClicks: any): number[][] {
  if (!firestoreClicks || !Array.isArray(firestoreClicks)) {
    return [];
  }

  // Check if it's already in the old format (nested arrays) - for backward compatibility
  if (firestoreClicks.length > 0 && Array.isArray(firestoreClicks[0])) {
    return firestoreClicks as number[][];
  }

  // Convert from object format to nested array format
  return firestoreClicks.map((item: any) => {
    if (
      typeof item === "object" &&
      item !== null &&
      ("click1" in item || "click2" in item)
    ) {
      return [item.click1 ?? -1, item.click2 ?? -1];
    }
    // Fallback for any other format
    return [-1, -1];
  });
}

// Service class
export class FirestoreService {
  /**
   * Check if competition metadata exists (to prevent duplicate creation)
   */
  static async competitionExists(competitionId: number): Promise<boolean> {
    try {
      const docRef = doc(db, `competitions/${competitionId}`);
      const docSnap = await getDoc(docRef);
      return docSnap.exists();
    } catch (error) {
      console.error("Error checking competition existence:", error);
      throw error;
    }
  }

  /**
   * Get competition metadata
   */
  static async getCompetitionMetadata(
    competitionId: number
  ): Promise<CompetitionMetadata | null> {
    try {
      const docRef = doc(db, `competitions/${competitionId}`);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docSnap.data() as CompetitionMetadata;
      }
      return null;
    } catch (error) {
      console.error("Error getting competition metadata:", error);
      throw error;
    }
  }

  /**
   * Create zurkhai structure for a competition
   * Called by Ерөнхий шүүгч (first time) or Admin
   */
  static async createZurkhai(
    competitionId: number,
    zurkhaiCount: number,
    onogUe: number,
    competitors: Competitor[],
    refereesIds: number[],
    userId: number,
    isAdmin: boolean,
    competitionData?: {
      name?: string;
      location?: string;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<void> {
    try {
      const batch = writeBatch(db);
      const exists = await this.competitionExists(competitionId);

      // If exists and user is not admin, throw error
      if (exists && !isAdmin) {
        throw new Error("Самбар аль хэдийн үүссэн байна");
      }

      // If admin and exists, archive old data first
      if (exists && isAdmin) {
        await this.archiveOldZurkhai(competitionId, zurkhaiCount);
      }

      // Create competition metadata
      const metadataRef = doc(db, `competitions/${competitionId}`);
      const metadata: CompetitionMetadata = {
        zurkhaiCount,
        onogUe,
        isLive: true,
        createdAt: Timestamp.now(),
        createdBy: userId,
        ...(exists && isAdmin
          ? {
              lastResetAt: Timestamp.now(),
              lastResetBy: userId,
            }
          : {}),
        ...(competitionData?.name ? { name: competitionData.name } : {}),
        ...(competitionData?.location
          ? { location: competitionData.location }
          : {}),
        ...(competitionData?.startDate
          ? { startDate: competitionData.startDate }
          : {}),
        ...(competitionData?.endDate
          ? { endDate: competitionData.endDate }
          : {}),
      };
      batch.set(metadataRef, metadata);

      // Distribute competitors across zurkhai (round-robin)
      const zurkhaiDistribution: Competitor[][] = Array.from(
        { length: zurkhaiCount },
        () => []
      );

      competitors.forEach((competitor, index) => {
        const zurkhaiIndex = index % zurkhaiCount;
        zurkhaiDistribution[zurkhaiIndex].push(competitor);
      });

      // Create zurkhai and scores
      for (let i = 0; i < zurkhaiCount; i++) {
        const zurkhaiIndex = i + 1;

        // Create zurkhai document with metadata fields
        const zurkhaiDocRef = doc(
          db,
          `competitions/${competitionId}/zurkhai/${zurkhaiIndex}`
        );

        // Build zurkhai metadata, only including refereeId if it exists
        const zurkhaiMetadata: any = {
          status: "active",
          createdAt: Timestamp.now(),
          createdBy: userId,
        };

        // Only add refereeId if it's defined
        if (refereesIds[i]) {
          zurkhaiMetadata.refereeId = refereesIds[i];
        }

        batch.set(zurkhaiDocRef, zurkhaiMetadata);

        // Create score documents for each competitor in this zurkhai
        const competitorsInZurkhai = zurkhaiDistribution[i];
        for (let i = 0; i < competitorsInZurkhai.length; i++) {
          const competitor = competitorsInZurkhai[i];
          const scoreRef = doc(
            db,
            `competitions/${competitionId}/zurkhai/${zurkhaiIndex}/scores/${competitor.id}`
          );

          // Build archer score object, excluding undefined values
          const archerScore: any = {
            index: i + 1,
            userId: competitor.id,
            userName: `${competitor.lastName} ${competitor.firstName}`,
            firstName: competitor.firstName,
            lastName: competitor.lastName,
            gender: competitor.gender,
            scores: Array(onogUe).fill(0),
            clicks: convertClicksToFirestore(
              Array(onogUe)
                .fill(null)
                .map(() => [-1, -1])
            ),
            totalScore: 0,
            hanaPoints: [],
            hasPoints: [],
            tovkhCount: 0,
            isAbsent: false,
            updatedAt: Timestamp.now(),
            updatedBy: userId,
            isComplete: false,
          };

          // Only add optional fields if they have values
          if (competitor.teamId) {
            archerScore.teamId = competitor.teamId;
          }
          if (competitor.team?.name) {
            archerScore.teamName = competitor.team.name;
          }
          if (competitor.rankId) {
            archerScore.rankId = competitor.rankId;
          }
          if (competitor.rank?.name) {
            archerScore.rankName = competitor.rank.name;
          }

          batch.set(scoreRef, archerScore);
        }
      }

      // Commit all writes
      await batch.commit();
    } catch (error) {
      console.error("Error creating zurkhai:", error);
      throw error;
    }
  }

  /**
   * Delete old zurkhai data (admin only)
   * This completely removes all score documents and zurkhai documents
   */
  static async archiveOldZurkhai(
    competitionId: number,
    zurkhaiCount: number
  ): Promise<void> {
    try {
      // Delete all zurkhai and their scores
      for (let i = 1; i <= zurkhaiCount; i++) {
        // Get all scores for this zurkhai
        const scoresRef = collection(
          db,
          `competitions/${competitionId}/zurkhai/${i}/scores`
        );
        const scoresSnapshot = await getDocs(scoresRef);

        // Delete all score documents in batches
        const deleteBatch = writeBatch(db);
        scoresSnapshot.docs.forEach((doc) => {
          deleteBatch.delete(doc.ref);
        });
        await deleteBatch.commit();

        // Delete the zurkhai document itself
        const zurkhaiDocRef = doc(
          db,
          `competitions/${competitionId}/zurkhai/${i}`
        );
        await deleteDoc(zurkhaiDocRef);
      }
    } catch (error) {
      console.error("Error deleting old zurkhai:", error);
      throw error;
    }
  }

  /**
   * Update score for an archer
   */
  static async updateScore(
    competitionId: number,
    zurkhaiIndex: number,
    userId: number,
    scores: number[],
    clicks: number[][],
    hanaPoints: number[],
    hasPoints: number[],
    updatedBy: number,
    archerMetadata?: {
      firstName: string;
      lastName: string;
      teamId?: number;
      teamName?: string;
      rankId?: number;
      rankName?: string;
      gender: "male" | "female";
    }
  ): Promise<void> {
    try {
      const scoreRef = doc(
        db,
        `competitions/${competitionId}/zurkhai/${zurkhaiIndex}/scores/${userId}`
      );

      // Clean scores array - replace null/undefined with 0
      const cleanScores = scores.map((s) => s ?? 0);

      // Calculate totalScore: only count scores where archer actually participated
      // (exclude rounds where clicks are [-1, -1] - not attended)
      const totalScore = cleanScores.reduce((sum, score, idx) => {
        const clicksForRound = clicks[idx] || [-1, -1];
        // Only count if not marked as not attended (both clicks are not -1)
        if (clicksForRound[0] !== -1 || clicksForRound[1] !== -1) {
          return sum + score;
        }
        return sum;
      }, 0);

      // isComplete: all rounds must be scored (not -1) and have score > 0
      const isComplete = cleanScores.every((score, idx) => {
        const clicksForRound = clicks[idx] || [-1, -1];
        // Round is complete if it has valid clicks (not both -1) and score > 0
        return (
          (clicksForRound[0] !== -1 || clicksForRound[1] !== -1) && score > 0
        );
      });

      // Convert nested array clicks to Firestore-compatible format
      const firestoreClicks = convertClicksToFirestore(clicks);

      const updateData: any = {
        scores: cleanScores,
        clicks: firestoreClicks,
        totalScore,
        hanaPoints,
        hasPoints,
        updatedAt: Timestamp.now(),
        updatedBy,
        isComplete,
      };

      // If archer metadata provided, include it (for first-time document creation)
      if (archerMetadata) {
        updateData.userId = userId;
        updateData.userName = `${archerMetadata.lastName} ${archerMetadata.firstName}`;
        updateData.firstName = archerMetadata.firstName;
        updateData.lastName = archerMetadata.lastName;
        updateData.gender = archerMetadata.gender;

        if (
          archerMetadata.teamId !== undefined &&
          archerMetadata.teamId !== null
        ) {
          updateData.teamId = archerMetadata.teamId;
        }
        if (
          archerMetadata.teamName !== undefined &&
          archerMetadata.teamName !== null
        ) {
          updateData.teamName = archerMetadata.teamName;
        }
        if (
          archerMetadata.rankId !== undefined &&
          archerMetadata.rankId !== null
        ) {
          updateData.rankId = archerMetadata.rankId;
        }
        if (
          archerMetadata.rankName !== undefined &&
          archerMetadata.rankName !== null
        ) {
          updateData.rankName = archerMetadata.rankName;
        }
      }

      // Remove any undefined values from updateData before sending to Firestore
      Object.keys(updateData).forEach((key) => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      await setDoc(scoreRef, updateData, { merge: true });
    } catch (error) {
      console.error("Error updating score:", error);
      throw error;
    }
  }

  /**
   * Mark archer as absent (Ирээгүй)
   */
  static async markArcherAbsent(
    competitionId: number,
    zurkhaiIndex: number,
    userId: number,
    isAbsent: boolean,
    updatedBy: number
  ): Promise<void> {
    try {
      const scoreRef = doc(
        db,
        `competitions/${competitionId}/zurkhai/${zurkhaiIndex}/scores/${userId}`
      );

      await updateDoc(scoreRef, {
        isAbsent,
        updatedAt: Timestamp.now(),
        updatedBy,
      });
    } catch (error) {
      console.error("Error updating archer status:", error);
      throw error;
    }
  }

  /**
   * Update Товх count (manual tiebreaker value)
   */
  static async updateTovkhCount(
    competitionId: number,
    zurkhaiIndex: number,
    userId: number,
    tovkhCount: number,
    updatedBy: number
  ): Promise<void> {
    try {
      const scoreRef = doc(
        db,
        `competitions/${competitionId}/zurkhai/${zurkhaiIndex}/scores/${userId}`
      );

      await updateDoc(scoreRef, {
        tovkhCount,
        updatedAt: Timestamp.now(),
        updatedBy,
      });
    } catch (error) {
      console.error("Error updating tovkh count:", error);
      throw error;
    }
  }

  /**
   * Update competition metadata in Firestore
   */
  static async updateCompetitionMetadata(
    competitionId: number,
    data: {
      name?: string;
      location?: string;
      startDate?: string;
      endDate?: string;
      zurkhaiCount?: number;
      onogUe?: number;
    }
  ): Promise<void> {
    try {
      const metadataRef = doc(db, `competitions/${competitionId}`);
      const docSnap = await getDoc(metadataRef);

      if (!docSnap.exists()) {
        // If document doesn't exist, we can't update it
        console.warn("Competition metadata does not exist in Firestore yet");
        return;
      }

      // Only update fields that are provided
      const updateData: any = {};
      if (data.name !== undefined) updateData.name = data.name;
      if (data.location !== undefined) updateData.location = data.location;
      if (data.startDate !== undefined) updateData.startDate = data.startDate;
      if (data.endDate !== undefined) updateData.endDate = data.endDate;
      if (data.zurkhaiCount !== undefined)
        updateData.zurkhaiCount = data.zurkhaiCount;
      if (data.onogUe !== undefined) updateData.onogUe = data.onogUe;

      if (Object.keys(updateData).length > 0) {
        await updateDoc(metadataRef, updateData);
      }
    } catch (error) {
      console.error("Error updating competition metadata:", error);
      throw error;
    }
  }

  /**
   * Listen to real-time competition metadata updates
   */
  static subscribeToCompetitionMetadata(
    competitionId: number,
    callback: (metadata: CompetitionMetadata | null) => void
  ): Unsubscribe {
    const metadataRef = doc(db, `competitions/${competitionId}`);

    return onSnapshot(
      metadataRef,
      (snapshot) => {
        if (snapshot.exists()) {
          callback(snapshot.data() as CompetitionMetadata);
        } else {
          callback(null);
        }
      },
      (error) => {
        console.error("Error listening to competition metadata:", error);
        callback(null);
      }
    );
  }

  /**
   * Listen to real-time score updates for a specific zurkhai
   */
  static subscribeToZurkhaiScores(
    competitionId: number,
    zurkhaiIndex: number,
    callback: (scores: ArcherScore[]) => void
  ): Unsubscribe {
    const scoresRef = collection(
      db,
      `competitions/${competitionId}/zurkhai/${zurkhaiIndex}/scores`
    );

    return onSnapshot(
      query(scoresRef),
      (snapshot) => {
        const scores: ArcherScore[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          // Convert clicks from Firestore format to nested array format
          if (data.clicks) {
            data.clicks = convertClicksFromFirestore(data.clicks);
          }
          scores.push(data as ArcherScore);
        });
        callback(scores);
      },
      (error) => {
        console.error("Error listening to scores:", error);
      }
    );
  }

  /**
   * Mark zurkhai as completed
   */
  static async completeZurkhai(
    competitionId: number,
    zurkhaiIndex: number
  ): Promise<void> {
    try {
      const zurkhaiDocRef = doc(
        db,
        `competitions/${competitionId}/zurkhai/${zurkhaiIndex}`
      );

      await updateDoc(zurkhaiDocRef, {
        status: "completed",
        completedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error("Error completing zurkhai:", error);
      throw error;
    }
  }

  /**
   * Get all scores for a competition (for final aggregation)
   */
  static async getAllCompetitionScores(
    competitionId: number,
    zurkhaiCount: number
  ): Promise<Map<number, ArcherScore[]>> {
    try {
      const allScores = new Map<number, ArcherScore[]>();

      for (let i = 1; i <= zurkhaiCount; i++) {
        const scoresRef = collection(
          db,
          `competitions/${competitionId}/zurkhai/${i}/scores`
        );
        const snapshot = await getDocs(scoresRef);

        const scores: ArcherScore[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          // Convert clicks from Firestore format to nested array format
          if (data.clicks) {
            data.clicks = convertClicksFromFirestore(data.clicks);
          }
          scores.push(data as ArcherScore);
        });

        allScores.set(i, scores);
      }

      return allScores;
    } catch (error) {
      console.error("Error getting all competition scores:", error);
      throw error;
    }
  }

  /**
   * Subscribe to aggregated scores across all zurkhai for a competition
   * Returns total hanaPoints and hasPoints for each archer
   */
  static subscribeToAggregatedScores(
    competitionId: number,
    callback: (
      scores: Map<
        number,
        { hanaPoints: number[]; hasPoints: number[]; totalScore: number }
      >
    ) => void
  ): Unsubscribe {
    let unsubscribers: Unsubscribe[] = [];
    let zurkhaiCount = 0;
    const allZurkhaiScores = new Map<number, Map<number, ArcherScore>>();

    // Function to aggregate scores from all zurkhai
    const aggregateScores = () => {
      const aggregated = new Map<
        number,
        { hanaPoints: number[]; hasPoints: number[]; totalScore: number }
      >();

      // Iterate through each zurkhai's scores
      allZurkhaiScores.forEach((zurkhaiScores, zurkhaiIndex) => {
        zurkhaiScores.forEach((archerScore, userId) => {
          if (!aggregated.has(userId)) {
            aggregated.set(userId, {
              hanaPoints: [],
              hasPoints: [],
              totalScore: 0,
            });
          }

          const current = aggregated.get(userId)!;
          current.hanaPoints.push(...(archerScore.hanaPoints || []));
          current.hasPoints.push(...(archerScore.hasPoints || []));
          current.totalScore += archerScore.totalScore || 0;
        });
      });

      callback(aggregated);
    };

    // First, subscribe to competition metadata to get zurkhaiCount
    const metadataRef = doc(db, `competitions/${competitionId}`);
    const metadataUnsubscribe = onSnapshot(
      metadataRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const metadata = snapshot.data() as CompetitionMetadata;
          const newZurkhaiCount = metadata.zurkhaiCount || 0;

          // If zurkhaiCount changed, re-subscribe to all zurkhai
          if (newZurkhaiCount !== zurkhaiCount) {
            zurkhaiCount = newZurkhaiCount;

            // Unsubscribe from all previous zurkhai listeners
            unsubscribers.forEach((unsub) => unsub());
            unsubscribers = [];
            allZurkhaiScores.clear();

            // Subscribe to each zurkhai
            for (let i = 1; i <= zurkhaiCount; i++) {
              const zurkhaiIndex = i;
              const scoresRef = collection(
                db,
                `competitions/${competitionId}/zurkhai/${zurkhaiIndex}/scores`
              );

              const unsubscribe = onSnapshot(
                query(scoresRef),
                (snapshot) => {
                  const scores = new Map<number, ArcherScore>();
                  snapshot.forEach((doc) => {
                    const data = doc.data();
                    // Convert clicks from Firestore format to nested array format
                    if (data.clicks) {
                      data.clicks = convertClicksFromFirestore(data.clicks);
                    }
                    scores.set(data.userId, data as ArcherScore);
                  });

                  allZurkhaiScores.set(zurkhaiIndex, scores);
                  aggregateScores();
                },
                (error) => {
                  console.error(
                    `Error listening to zurkhai ${zurkhaiIndex} scores:`,
                    error
                  );
                }
              );

              unsubscribers.push(unsubscribe);
            }
          }
        }
      },
      (error) => {
        console.error("Error listening to competition metadata:", error);
      }
    );

    // Return unsubscribe function that cleans up all listeners
    return () => {
      metadataUnsubscribe();
      unsubscribers.forEach((unsub) => unsub());
    };
  }
}

export default FirestoreService;
